import React from "react";
import { toast } from "react-toastify";
import { MessageCircle } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import Navbar from "../../../Components/Common/navbar";
import { useAuthContext } from "../../../Components/AuthContext";
import MobileViewContainer from "../../../Components/MobileViewContainer";
import { eventService } from "../../../api/services/event.service";
import { shareKakao } from "../../../common/social/kakao/api";
import PromotionGiftLayout from "../../Promotion/components/PromotionGiftLayout";

const DEFAULT_VOUCHER_STATUS = {
  downloaded: false,
  remain: 0,
};

function getErrorMessage(error) {
  if (!error) {
    return "";
  }

  if (typeof error === "string") {
    return error;
  }

  return error?.response?.data?.error || error?.response?.data?.detail || error?.message || "정보를 불러오지 못했습니다.";
}

function mapEventVoucherItem(voucherItem, remainingCount) {
  const siteName = voucherItem?.site?.name;
  const startDate = voucherItem?.start_date;
  const endDate = voucherItem?.end_date;

  return {
    type: "voucher",
    itemName: voucherItem?.voucher_product?.name || "",
    imageUrl: voucherItem?.voucher_product?.image || "",
    senderName: siteName || "",
    remainingCount,
    siteLines: siteName ? [siteName] : [],
    validityText: startDate && endDate ? `${startDate} ~ ${endDate} 사용 가능` : "유효 기간 내 사용 가능",
    noticeHtml: voucherItem?.voucher_product?.precautions || "",
  };
}

const EventVoucherPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { accessToken } = useAuthContext();

  const isLoggedIn = Boolean(accessToken);

  const [voucherItem, setVoucherItem] = React.useState(null);
  const [voucherStatus, setVoucherStatus] = React.useState(DEFAULT_VOUCHER_STATUS);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState(null);

  const fetchVoucherRule = React.useCallback(async () => {
    if (!id) {
      setVoucherItem(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await eventService.getBookRule(id);
      const nextItem = response?.data;
      setVoucherItem(nextItem?.voucher_product ? nextItem : null);
    } catch (e) {
      console.log(e);
      setError(e);
      setVoucherItem(null);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const fetchVoucherStatus = React.useCallback(async () => {
    if (!id || !isLoggedIn) {
      setVoucherStatus(DEFAULT_VOUCHER_STATUS);
      return;
    }

    try {
      const response = await eventService.checkBook(id);
      const downloaded = Boolean(response?.data?.downloaded);
      const remainNumber = Number(response?.data?.remain);

      setVoucherStatus({
        downloaded,
        remain: Number.isFinite(remainNumber) ? remainNumber : 0,
      });
    } catch (e) {
      console.log(e);
      setVoucherStatus(DEFAULT_VOUCHER_STATUS);
    }
  }, [id, isLoggedIn]);

  React.useEffect(() => {
    fetchVoucherRule();
  }, [fetchVoucherRule]);

  React.useEffect(() => {
    fetchVoucherStatus();
  }, [fetchVoucherStatus]);

  const isReceived = isLoggedIn && voucherStatus.downloaded;
  const isEventOpen = voucherItem?.state === "노출중";

  const mappedItem = React.useMemo(() => {
    const remainingCount = isLoggedIn ? voucherStatus.remain : 0;
    return mapEventVoucherItem(voucherItem, remainingCount);
  }, [isLoggedIn, voucherItem, voucherStatus.remain]);

  const handlePrimaryAction = React.useCallback(async () => {
    if (!id) {
      return;
    }

    if (!isLoggedIn) {
      const redirectPath = `${location.pathname}${location.search || ""}`;
      navigate(`/login?path=${redirectPath}`);
      return;
    }

    if (!voucherItem || isSubmitting) {
      return;
    }

    if (!isEventOpen) {
      toast.error("이벤트가 종료되었습니다.");
      return;
    }

    if (isReceived) {
      toast.error("이미 교환권을 받으셨습니다.");
      return;
    }

    setIsSubmitting(true);

    try {
      await eventService.downloadVoucherBook(Number(id));
      toast.error("교환권을 받았습니다.");
      await fetchVoucherStatus();
    } catch (e) {
      console.log(e);
      setError(e);
      toast.error(getErrorMessage(e) || "교환권을 받을 수 없습니다.");
    } finally {
      setIsSubmitting(false);
    }
  }, [fetchVoucherStatus, id, isEventOpen, isLoggedIn, isReceived, isSubmitting, location.pathname, location.search, navigate, voucherItem]);

  const handleShare = React.useCallback(() => {
    if (!voucherItem) {
      return;
    }

    const imageUrl = voucherItem?.voucher_product?.image || `https://${window.location.hostname}/amazing-logo.png`;

    shareKakao({
      title: voucherItem?.site?.name || "AmazingTicket",
      description: voucherItem?.voucher_product?.name || "무료쿠폰",
      imageUrl,
      shareUrl: window.location.href,
    });
  }, [voucherItem]);

  const bottomAction = voucherItem ? (
    <div className="rounded-[24px] border border-[#e4e3e0] bg-white px-6 py-6">
      {!isEventOpen ? (
        <p className="mb-4 text-center text-[17px] text-[#ff5d12]" style={{ fontFamily: "Gmarket-M" }}>
          이벤트가 종료되었습니다.
        </p>
      ) : null}

      <button
        type="button"
        onClick={handleShare}
        className="flex w-full items-center justify-center gap-2 rounded-[20px] px-6 py-4 text-[18px] text-white transition-opacity disabled:cursor-not-allowed disabled:bg-[#b8c0cd]"
        style={{
          fontFamily: "Gmarket-B",
          background: "linear-gradient(135deg, #ff7800 0%, #ff2d57 100%)",
        }}
        disabled={isLoading}
      >
        <MessageCircle size={22} />
        <span>카카오톡 공유하기</span>
      </button>
    </div>
  ) : null;

  return (
    <MobileViewContainer>
      <Navbar />
      <PromotionGiftLayout
        {...mappedItem}
        isLoggedIn={isLoggedIn}
        isLoading={isLoading}
        isSubmitting={isSubmitting}
        isReceived={isReceived}
        onPrimaryAction={handlePrimaryAction}
        error={error}
        bottomAction={bottomAction}
      />
    </MobileViewContainer>
  );
};

export default EventVoucherPage;
